import React, { useState, useEffect, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useLocation, Outlet } from 'react-router-dom';
import lazyWithRetry from './utils/lazyWithRetry';
import RouteErrorBoundary from './components/RouteErrorBoundary';
import AppErrorBoundary from './components/AppErrorBoundary';
import { AcademicCapIcon, DocumentTextIcon, ChartBarIcon, ChatBubbleLeftRightIcon, ArrowRightOnRectangleIcon, BookOpenIcon, BuildingLibraryIcon, SparklesIcon, RocketLaunchIcon, StarIcon } from '@heroicons/react/24/outline';
const ResourcesPage = lazyWithRetry(() => import('./pages/ResourcesPage'), 'ResourcesPage');
const FeedbackPage = lazyWithRetry(() => import('./pages/FeedbackPage'), 'FeedbackPage');
const EmailSignInFinish = lazyWithRetry(() => import('./pages/EmailSignInFinish'), 'EmailSignInFinish');
// Meta's data deletion status page (#5154): the link Meta hands a person.
const FacebookDeletionStatus = lazyWithRetry(() => import('./pages/FacebookDeletionStatus'), 'FacebookDeletionStatus');
const ResourcePaperPage = lazyWithRetry(() => import('./pages/ResourcePaperPage'), 'ResourcePaperPage');
// News (#2575, founder pick A): dated, sourced stories from data/news.
const NewsIndexPage = lazyWithRetry(() => import('./pages/NewsIndexPage'), 'NewsIndexPage');
const NewsArticlePage = lazyWithRetry(() => import('./pages/NewsArticlePage'), 'NewsArticlePage');
// Public college pages (#2579): official facts only, from data/colleges.
const CollegesIndexPage = lazyWithRetry(() => import('./pages/CollegesIndexPage'), 'CollegesIndexPage');
const VideosPage = lazyWithRetry(() => import('./pages/VideosPage'), 'VideosPage');
const CollegePage = lazyWithRetry(() => import('./pages/CollegePage'), 'CollegePage');
// /got-in (#4726): the private outcomes form every Short's caption points to.
const OutcomeSharePage = lazyWithRetry(() => import('./pages/OutcomeSharePage'), 'OutcomeSharePage');
// /survey/pmf (#5623): where the survey email's answer links land.
const SurveyThanksPage = lazyWithRetry(() => import('./pages/SurveyThanksPage'), 'SurveyThanksPage');
const MastersPage = lazyWithRetry(() => import('./pages/MastersPage'), 'MastersPage');
// /ask/<short> (#3262): a Short's end-card link, forwarded to the college page with Astra open.
const AskRedirect = lazyWithRetry(() => import('./pages/AskRedirect'), 'AskRedirect');
const ClaimCreditsPage = lazyWithRetry(() => import('./pages/ClaimCreditsPage'), 'ClaimCreditsPage');
const SharePage = lazyWithRetry(() => import('./pages/SharePage'), 'SharePage');
const ReviewGuestPage = lazyWithRetry(() => import('./pages/ReviewGuestPage'), 'ReviewGuestPage');
const CounselorPage = lazyWithRetry(() => import('./pages/CounselorPage'), 'CounselorPage');
const ActivityIdeasPage = lazyWithRetry(() => import('./pages/ActivityIdeasPage'), 'ActivityIdeasPage');
const PatentLabPage = lazyWithRetry(() => import('./pages/PatentLabPage'), 'PatentLabPage');
const BuilderPackPage = lazyWithRetry(() => import('./pages/BuilderPackPage'), 'BuilderPackPage');
const PitchLabPage = lazyWithRetry(() => import('./pages/PitchLabPage'), 'PitchLabPage');
const ResearchLabPage = lazyWithRetry(() => import('./pages/ResearchLabPage'), 'ResearchLabPage');
const ShareYourStoryPage = lazyWithRetry(() => import('./pages/ShareYourStoryPage'), 'ShareYourStoryPage');
const SettingsPage = lazyWithRetry(() => import('./pages/SettingsPage'), 'SettingsPage');
import LowCreditsBanner from './components/LowCreditsBanner';
import ConciergeLauncher from './components/concierge/ConciergeLauncher';
import QuickTour from './components/QuickTour';
import { setQuickTourState } from './services/quickTourState';
const Profile = lazyWithRetry(() => import('./pages/Profile'), 'Profile');
const UniversityExplorer = lazyWithRetry(() => import('./pages/UniversityExplorer'), 'UniversityExplorer');
const StratiaLaunchpad = lazyWithRetry(() => import('./pages/StratiaLaunchpad'), 'StratiaLaunchpad');
const AstraTodayPage = lazyWithRetry(() => import('./pages/AstraTodayPage'), 'AstraTodayPage');
const ParentHomePage = lazyWithRetry(() => import('./pages/ParentHomePage'), 'ParentHomePage');
const MajorMap = lazyWithRetry(() => import('./pages/MajorMap'), 'MajorMap');
const DecisionLedgerPage = lazyWithRetry(() => import('./pages/DecisionLedgerPage'), 'DecisionLedgerPage');
const EssayHelpPage = lazyWithRetry(() => import('./pages/EssayHelpPage'), 'EssayHelpPage');
// `/` renders the landing page from the first paint: LandingRoute imports it
// eagerly and waits on no flag (#2526; a gate here flashed the old page).
import LandingRoute from './pages/LandingRoute';
const PricingPage = lazyWithRetry(() => import('./pages/PricingPage'), 'PricingPage');
// Paid landing surface for the Major-door ad group. Lazy: it carries a 35KB
// gzipped dataset nobody else needs.
const MajorDoorsPage = lazyWithRetry(() => import('./pages/MajorDoorsPage'), 'MajorDoorsPage');
const ContactPage = lazyWithRetry(() => import('./pages/ContactPage'), 'ContactPage');
// The Team and For Schools pages: public, ungated.
const TeamPage = lazyWithRetry(() => import('./pages/TeamPage'), 'TeamPage');
const ForSchoolsPage = lazyWithRetry(() => import('./pages/ForSchoolsPage'), 'ForSchoolsPage');
const PaymentSuccess = lazyWithRetry(() => import('./pages/PaymentSuccess'), 'PaymentSuccess');
const PrivacyPolicy = lazyWithRetry(() => import('./pages/PrivacyPolicy'), 'PrivacyPolicy');
const StudentDataPrinciples = lazyWithRetry(() => import('./pages/StudentDataPrinciples'), 'StudentDataPrinciples');
const PhotoCreditsPage = lazyWithRetry(() => import('./pages/PhotoCreditsPage'), 'PhotoCreditsPage');
const TeamDevicePage = lazyWithRetry(() => import('./pages/TeamDevicePage'), 'TeamDevicePage');
const PatentsPage = lazyWithRetry(() => import('./pages/PatentsPage'), 'PatentsPage');
// Every product screen, one at a time (decision A, 2026-09-29, #2513).
const TourPage = lazyWithRetry(() => import('./pages/TourPage'), 'TourPage');
const TermsOfService = lazyWithRetry(() => import('./pages/TermsOfService'), 'TermsOfService');
const FinancialAidComparison = lazyWithRetry(() => import('./pages/FinancialAidComparison'), 'FinancialAidComparison');
const RoadmapPage = lazyWithRetry(() => import('./pages/RoadmapPage'), 'RoadmapPage');
const SummerProgramsPage = lazyWithRetry(() => import('./pages/SummerProgramsPage'), 'SummerProgramsPage');
const CampusEventsPage = lazyWithRetry(() => import('./pages/CampusEventsPage'), 'CampusEventsPage');
const ResearchNotebook = lazyWithRetry(() => import('./pages/ResearchNotebook'), 'ResearchNotebook');
const ConnectAgents = lazyWithRetry(() => import('./pages/ConnectAgents'), 'ConnectAgents');
const CommandCenter = lazyWithRetry(() => import('./pages/CommandCenter'), 'CommandCenter');
// Astra's own page (#2210) — the same conversation the drawer runs, given
// a thread rail and the width to read in.
const AstraPage = lazyWithRetry(() => import('./pages/AstraPage'), 'AstraPage');
import ProtectedRoute from './components/auth/ProtectedRoute';
import Navigation from './components/Navigation';
import FooterV2 from './components/landing/v2/FooterV2';
import { SidebarProvider, useSidebar, sidebarContentPad } from './context/SidebarContext';
const WelcomePage = lazyWithRetry(() => import('./pages/WelcomePage'), 'WelcomePage');
const StartPage = lazyWithRetry(() => import('./pages/StartPage'), 'StartPage');
import AssentGate from './components/AssentGate';
import TrialStrip from './components/trial/TrialStrip';
import useAssentStatus from './hooks/useAssentStatus';
import GettingStartedCard from './components/GettingStartedCard';
import { DetailViewProvider, useDetailView } from './context/DetailViewContext';
import { NAV_TOP_LEVEL_PATHS } from './components/Navigation';
import YourYearPlan from './components/YourYearPlan';
import { AuthProvider, useAuth } from './context/AuthContext';
import { FeatureFlagsProvider } from './context/FeatureFlagsContext';
import RedirectSignInHandler from './components/RedirectSignInHandler';
import SignInSheet from './components/SignInSheet';
import { SecondAccountHint, SignInLinkOffer } from './components/SignInAfter';
import InstallPrompt from './components/InstallPrompt';
import PushAsk from './components/astraToday/PushAsk';
import { ApproachProvider } from './context/ApproachContext';
import { PaymentProvider, usePayment } from './context/PaymentContext';
import { ToastProvider } from './components/Toast';
import UpgradeModal from './components/UpgradeModal';
import SessionTimeout from './components/SessionTimeout';
import FirstReadBar from './components/firstRead/FirstReadBar';
import { fetchUserProfile, sendWelcomeEmail } from './services/api';
import { hasRealProfileData } from './utils/profileMeta';
import { isPhoneViewport } from './utils/postSignIn';
import { shouldAutoOpenTour } from './utils/tourGate';
import { reportAstraLiteJoin } from './services/astraLite';
import ScrollToTop from './components/ScrollToTop';
import { ASTRA_PATH } from './utils/astraLink';
import AnalyticsListener from './components/AnalyticsListener';
import './index.css';

// Navigation lives in components/Navigation.jsx — a single auth-aware
// component shared by AppLayout (protected pages) and the public Resources
// routes. Logged-out visitors see only the Resources link + a Try Stratia
// CTA; signed-in users see the full nav.

// Layout component for protected routes. New users (no profile) are routed to
// the full-screen /welcome journey (#560) instead of the old blocking modal;
// the GettingStartedCard carries the journey after it.
function JourneyBanners({ pathname }) {
  const { detailOpen } = useDetailView();
  if (detailOpen || !NAV_TOP_LEVEL_PATHS.has(pathname)) return null;
  return (
    <>
      {/* Getting-started checklist (#561) — follows the user until every
          item auto-checks from real data, or they dismiss it. */}
      <GettingStartedCard />
      {/* "Your Year" (#1240): the grade-differentiated plan that carries
          engagement AFTER onboarding. Renders nothing without a grade. */}
      <YourYearPlan />
    </>
  );
}
function AppLayout() {
  const { currentUser } = useAuth();
  const { collapsed } = useSidebar();
  const [showTour, setShowTour] = useState(false);
  const assented = useAssentStatus(currentUser?.email) === 'assented';
  // Keys the page-content error boundary: navigating away from a crashed
  // page resets it, so nav/sidebar/Astra always survive a page crash.
  const location = useLocation();

  // Tell Astra Today's notification ask whether the tour is up or still being
  // decided, so it never opens underneath it (#4234).
  const [tourDecided, setTourDecided] = useState(false);
  useEffect(() => {
    if (!currentUser?.email) { setQuickTourState('none'); return undefined; }
    // The tour itself publishes 'open' while it is on screen (QuickTour); a
    // tour waiting on assent stays 'pending', as the ask waits on assent too.
    if (!tourDecided) setQuickTourState('pending');
    else if (!showTour) setQuickTourState('none');
    return undefined;
  }, [currentUser?.email, showTour, tourDecided]);
  useEffect(() => () => setQuickTourState('none'), []);

  useEffect(() => {
    const checkOnboarding = async () => {
      if (!currentUser?.email) return;
      // Astra Lite (#2549): a browser that pressed Start free on the free
      // read's limit card flips one boolean on its content-free record.
      reportAstraLiteJoin();

      // The quick tour is gated ONLY by its own seen flag — one showing per
      // user, whether or not they've built a profile yet.
      const tourKey = `quick_tour_seen_${currentUser.email}`;

      // localStorage (not sessionStorage): a completed or skipped onboarding
      // must never re-nag on the next visit. Known-returning users on this
      // device skip the profile fetch; they still get the tour once.
      const completedKey = `onboarding_completed_${currentUser.email}`;
      const skippedKey = `onboarding_skipped_${currentUser.email}`;
      if (localStorage.getItem(completedKey) || localStorage.getItem(skippedKey)) {
        // One shared rule (utils/tourGate) — this branch used to decide for
        // itself and never checked the device, which is how the tour fired on
        // the universities page the instant /welcome finished.
        if (shouldAutoOpenTour({
          tourSeen: !!localStorage.getItem(tourKey),
          isPhone: isPhoneViewport(),
        })) setShowTour(true);
        return;
      }

      try {
        const profileResult = await fetchUserProfile(currentUser.email);
        const hasContent = profileResult.content && profileResult.content.length > 100;
        // Meta-only docs (visit history, tour flags) are NOT a profile —
        // treating them as one suppressed the tour and faked onboarded state.
        const hasStructuredProfile = hasRealProfileData(profileResult.profile);
        const tourSeen = profileResult.profile?.quick_tour_seen || localStorage.getItem(tourKey);

        if (profileResult.success && (hasContent || hasStructuredProfile)) {
          localStorage.setItem(completedKey, 'true');
          if (!tourSeen) setShowTour(true);
        } else if (profileResult.error === 'No profile found') {
          // Definitively a brand-new user (a timeout or 5xx keeps its own
          // message, so a transient failure never lands here). No forced
          // journey: greet them with the quick tour — the Getting-started
          // checklist owns the doing from here. /welcome stays reachable
          // by URL but is no longer the front door.
          const welcomeEmailKey = `welcome_email_sent_${currentUser.email}`;
          if (!localStorage.getItem(welcomeEmailKey)) {
            sendWelcomeEmail(currentUser.email).then(result => {
              if (result.success) localStorage.setItem(welcomeEmailKey, 'true');
            }).catch(err => console.error('[App] Welcome email failed:', err));
          }
          // A draft carried over from the landing try-hero means the user is
          // mid-flow (the scratchpad is auto-processing it into their profile
          // review) — popping the tour over that reads as an interruption
          // (session replays, 2026-07-18). They get the tour on a later visit.
          const carriedDraft = localStorage.getItem('scratchpad_draft_anonymous')
            || localStorage.getItem(`scratchpad_draft_${currentUser.email}`);
          // Same reasoning on a phone: a brand-new mobile user is being sent
          // into the /welcome journey, and a modal over a full-screen
          // first-run flow is two asks stacked on one small screen. They get
          // the tour on a later visit, exactly like the carried-draft case.
          if (shouldAutoOpenTour({
            tourSeen: !!tourSeen,
            isPhone: isPhoneViewport(),
            carriedDraft: !!carriedDraft,
          })) setShowTour(true);
        }
      } catch (error) {
        // On error, do nothing — never block a returning user.
        console.error('[App] Error checking profile:', error);
      }
    };

    // A read for an account that is no longer signed in never decides for
    // the next one (reviewer, #4234).
    let live = true;
    setTourDecided(false);
    checkOnboarding().finally(() => { if (live) setTourDecided(true); });
    return () => { live = false; };
  }, [currentUser?.email]);

  return (
    <div className="min-h-screen bg-[#FDFCF7]">
      {/* Terms/Privacy + 13-or-older click-through (compliance fix 2). Lives
          on AppLayout itself, NOT inside the nav-page-gated extras, so it
          covers every signed-in page including detail views. */}
      <AssentGate />
      <Navigation />
      {/* The docked first-read bar (#2359) publishes its height; the page
          keeps that much room at the bottom so the bar never hides content. */}
      <main className={`${sidebarContentPad(collapsed)} transition-[padding] duration-300`}
            style={{ paddingBottom: 'var(--first-read-bar-h, 0px)' }}>
        {/* pb-28 below lg: Astra's pill (ConciergeLauncher, phones only,
            1.5rem off the bottom and about 3.5rem tall) floats over the
            page's end, and the last lines of a page sat under it with no
            way to scroll them clear (#3738: the Insights verdict). Only
            where the pill shows: /astra sizes its own box to the viewport. */}
        <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 ${
          location.pathname !== ASTRA_PATH ? 'pb-28' : 'pb-8'} lg:pb-8`}>
          {/* The first-read bar (#2359, founder pick C): the student's first
              starting-picture school, on every signed-in page until the list
              holds one. Self-gating; docks at the bottom on a phone. */}
          <FirstReadBar />
          {/* Journey banners live ONLY on nav-item pages (founder,
              2026-08-09): route-gated to the sidebar's own destinations,
              and detail views opened INSIDE a route (fit analysis,
              university profile) suppress them via DetailViewMarker. */}
          <JourneyBanners pathname={location.pathname} />
          {/* Unknown page crashes show branded copy, never the error, and
              reset on navigation (founder 2026-08-06). */}
          <AppErrorBoundary key={location.pathname}>
            <Outlet />
          </AppErrorBoundary>
        </div>
      </main>
      {/* One-time first-login tour: teaches the app's sequence (profile →
          direction → list → plan) so new users don't burn credits adding
          colleges before their profile exists.
          Gated on assent (#1750): the tour and the gate both fire on a brand-new
          account, and the tour was running its steps underneath the unaccepted
          modal — onboarding must not begin before the 13+/Terms attestation. */}
      {showTour && assented && (
        <QuickTour email={currentUser?.email} onClose={() => setShowTour(false)} />
      )}
    </div>
  );
}

// Lightweight fallback while a lazy route chunk loads. The landing page is
// eager (imported statically), so `/` never shows this — only in-app
// navigations to a not-yet-loaded route do.
function RouteFallback() {
  return (
    <div className="min-h-screen bg-[#FDFCF7] flex items-center justify-center">
      <div className="h-8 w-8 rounded-full border-2 border-[#1A4D2E] border-t-transparent animate-spin" />
    </div>
  );
}

// A renamed route's old path, kept alive with its query string intact.
function LegacyRedirect({ to }) {
  const { search, hash } = useLocation();
  return <Navigate to={`${to}${search}${hash}`} replace />;
}

function App() {
  return (
    <RouteErrorBoundary>
    <Router>
      <ScrollToTop />
      <AuthProvider>
        <SessionTimeout />
        <InstallPrompt />
        <FeatureFlagsProvider>
          {/* Inside the flags provider since fix 4: identity mode (hashed
              analytics id) is flag-driven. The listener depends only on
              Router + Auth + Flags — safe at this depth. */}
          <AnalyticsListener />
        <PaymentProvider>
          <ApproachProvider>
            <ToastProvider>
              {/* Inside the toasts since #2457: a sign-in that died
                  mid-flight is announced here, with its retry. */}
              <RedirectSignInHandler />
              {/* The sign-in sheet (#3928): Google, or a link by email. Opens
                  only once the email link is live; Google-only until then. */}
              <SignInSheet />
              {/* After a sign-in (#5154): "Also sign in with Apple next
                  time?" and the second-account line, each once. */}
              <SignInLinkOffer />
              <SecondAccountHint />
              <SidebarProvider>
              <DetailViewProvider>
              {/* Global low-credits nudge — self-gating (signed-in + low only),
                  so it shows on BOTH marketing pages (e.g. /pricing) and the app,
                  not just the protected AppLayout. */}
              <LowCreditsBanner />
              {/* The 7-day trial's one line (#4703): its end date, self-gating. */}
              <TrialStrip />
              {/* Concierge onboarding greeter — flag-gated, self-gating on
                  profile-empty (see ConciergeLauncher for the full rule). */}
              <ConciergeLauncher />
              {/* Astra Today's notification ask (C1, #3756): after a useful
                  moment, pilot students only; self-gating. */}
              <PushAsk />
              <Suspense fallback={<RouteFallback />}>
              <Routes>
                {/* Public routes */}
                <Route path="/" element={<LandingRoute />} />
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="/major-doors" element={<MajorDoorsPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/team" element={<TeamPage />} />
                <Route path="/for-schools" element={<ForSchoolsPage />} />
                {/* Public on purpose: the outreach email links straight here —
                    nobody should have to sign in to hand us a smiley. */}
                <Route path="/feedback" element={<FeedbackPage />} />
                {/* Claim link from the free-credits email. Public route on
                    purpose: a signed-out recipient must land somewhere that
                    explains itself and offers sign-in, not a 404. The credits
                    still only move for the verified recipient. */}
                <Route path="/claim-credits" element={<ClaimCreditsPage />} />
                {/* Where the emailed sign-in link lands (#3928). Public by
                    definition: the student is signing in here. */}
                <Route path="/sign-in/finish" element={<EmailSignInFinish />} />
                <Route path="/facebook-deletion" element={<FacebookDeletionStatus />} />
                <Route path="/payment-success" element={<PaymentSuccess />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/student-data-principles" element={<StudentDataPrinciples />} />
                <Route path="/photo-credits" element={<PhotoCreditsPage />} />
                {/* Marks a founder-household browser as ours (#3021). Public on
                    purpose (signed-out browsing is what it fixes); the key in
                    the link is checked server-side before anything is stored. */}
                <Route path="/team-device" element={<TeamDevicePage />} />
                <Route path="/terms" element={<TermsOfService />} />
                <Route path="/patents" element={<PatentsPage />} />
                <Route path="/tour" element={<TourPage />} />
                {/* Every public Short and college tutorial (#4728, founder "A1"
                    2026-10-18): the landing ribbon's "Watch them". PUBLIC, like
                    /colleges. It replaced the redirect to /#film that the
                    retired tours library left (#2243, #2530); old email links
                    to /videos and /videos?v=... land here now. */}
                <Route path="/videos" element={<VideosPage />} />
                {/* Resources & whitepapers — public so prospective users
                    (parents, counselors) can find them without signing in. */}
                <Route path="/news" element={<NewsIndexPage />} />
                <Route path="/news/:slug" element={<NewsArticlePage />} />
                <Route path="/colleges" element={<CollegesIndexPage />} />
                <Route path="/colleges/:slug" element={<CollegePage />} />
                <Route path="/ask/:short" element={<AskRedirect />} />
                {/* #5267: the bare /ask opens Astra on the home page, never the sign-in */}
                <Route path="/ask" element={<AskRedirect bare />} />
                <Route path="/resources" element={<ResourcesPage />} />
                {/* Share what got you in (#4726, ADR 0056): PUBLIC, no sign-in
                    and no name; every Short's caption points here. Not /share,
                    which is the signed-in Collaborative Review page. */}
                <Route path="/got-in" element={<OutcomeSharePage />} />
                {/* The survey email's thank-you page (#5623): PUBLIC, no
                    sign-in; the tap was recorded by the email's own link.
                    Not in the public route list (noindex, reached only from
                    the email). */}
                <Route path="/survey/pmf" element={<SurveyThanksPage />} />
                {/* Master's for every region (#4985, founder pick A): PUBLIC and
                    dark behind india_masters; flag off, the page sends the
                    visitor home. Not in the public route list (no prerender,
                    no sitemap) until the flag goes on for everyone. */}
                <Route path="/masters" element={<MastersPage />} />
                {/* Collaborative Review guest viewer (#846): PUBLIC — invited
                    reviewers hold a magic link, never a Firebase account. The
                    gateway enforces flag + token server-side; this route just
                    renders what it's granted (or a designed degraded state). */}
                <Route path="/review/:grantId" element={<ReviewGuestPage />} />
                {/* Counselor caseload (#1991): PUBLIC — counselors hold a
                    magic link, never an account. Flag + session enforced by
                    the gateway; dark reads as a designed 'not open yet'. */}
                <Route path="/counselor" element={<CounselorPage />} />
                <Route path="/resources/:slug" element={<ResourcePaperPage />} />

                {/* Onboarding journey (#560) — protected but OUTSIDE AppLayout:
                    a full-screen takeover with no sidebar, so a brand-new user
                    meets one focused flow instead of a modal over an app they
                    have never seen. */}
                <Route
                  path="/welcome"
                  element={
                    <ProtectedRoute>
                      {/* The assent gate must land BEFORE the journey collects
                          anything (reviewer-caught: a new signup reaches
                          /welcome first, and the gate exists precisely so no
                          data is collected ahead of the 13+ attestation).
                          Double-mounting with AppLayout is harmless — the
                          version-keyed belt makes the second instance null. */}
                      <AssentGate />
                      <WelcomePage />
                    </ProtectedRoute>
                  }
                />

                {/* Profile-first sign-up (#4702): GPA, a test score or none,
                    an intended major and three colleges, then the list read.
                    Same takeover shape as /welcome, behind the
                    profile_first_signup flag (the page sends anyone else on). */}
                <Route
                  path="/start"
                  element={
                    <ProtectedRoute>
                      <AssentGate />
                      <StartPage />
                    </ProtectedRoute>
                  }
                />

                {/* Public pages that still get the app shell: the sidebar is
                    the way BACK into the app, so it must never disappear.
                    Signed-out visitors see the public nav entries only. */}
                <Route element={<AppLayout />}>
                </Route>

                {/* Protected routes */}
                <Route
                  element={
                    <ProtectedRoute>
                      <AppLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route path="/profile" element={<Profile />} />
                  <Route path="/settings" element={<SettingsPage />} />
                  {/* Collaborative Review (#845). */}
                  <Route path="/share" element={<SharePage />} />
                  {/* My Advisor - DISABLED FOR MVP */}
                  <Route path="/universities" element={<UniversityExplorer />} />
                  <Route path="/launchpad" element={<StratiaLaunchpad />} />
                  {/* Astra Today (#3754, ADR 0041): a pilot student's landing page. */}
                  <Route path="/today" element={<AstraTodayPage />} />
                  {/* A parent's home (#5811): the parent read of the account's
                      own list; anyone else goes on to the Launchpad. */}
                  <Route path="/home" element={<ParentHomePage />} />
                  <Route path="/decision-ledger" element={<DecisionLedgerPage />} />
                  <Route path="/major-map" element={<MajorMap />} />
                  <Route path="/activities" element={<ActivityIdeasPage />} />
                  <Route path="/patent-lab" element={<PatentLabPage />} />
                  <Route path="/pitch-lab" element={<PitchLabPage />} />
                  <Route path="/research-lab" element={<ResearchLabPage />} />
                  {/* /builder is the Build group's home (#2389, founder pick A
                      2026-09-29; named in content/buildNames.js, #2394). It
                      was the Patent Lab until 2026-09-21 and a redirect after;
                      Astra's older export link and an email in 91 inboxes
                      still point at /builder?export=1, so the home forwards
                      that one arrival to /patent-lab with the query. */}
                  <Route path="/builder" element={<BuilderPackPage />} />
                  <Route path="/share-your-story" element={<ShareYourStoryPage />} />
                  <Route path="/roadmap" element={<RoadmapPage />} />
                  <Route path="/programs" element={<SummerProgramsPage />} />
                  {/* Visits & Events (#1326). Dark until the founder opens the
                      flag; the backend 404s for anyone it is not on for, so the
                      route existing does not expose anything. */}
                  <Route path="/events" element={<CampusEventsPage />} />
                  {/* Renamed 2026-08-09 (the catalog was never summer-only).
                      The old path stays forever: sent digest emails, the
                      Roadmap's "Consider:" deep links, and any bookmark still
                      point at it — and those carry ?program=&from=, which a
                      bare <Navigate to="/programs"> would DROP, landing the
                      student on the catalog instead of the program they
                      clicked. `replace` keeps it out of history so Back
                      doesn't bounce back into the redirect. */}
                  <Route path="/summer-programs" element={<LegacyRedirect to="/programs" />} />
                  <Route path="/research" element={<ResearchNotebook />} />
                  <Route path="/astra" element={<AstraPage />} />
                  <Route path="/connect" element={<ConnectAgents />} />
                  <Route path="/command-center" element={<CommandCenter />} />
                  {/* Legacy routes redirect to the unified /roadmap timeline,
                      pre-selecting the matching filter where one applies.
                      `replace` so the back-button doesn't bounce between the
                      old and new URL. */}
                  <Route path="/counselor" element={<Navigate to="/roadmap" replace />} />
                  <Route path="/progress" element={<Navigate to="/roadmap?filter=essays" replace />} />
                  <Route path="/essays" element={<Navigate to="/roadmap?filter=essays" replace />} />
                  <Route path="/applications" element={<Navigate to="/roadmap?filter=deadlines" replace />} />
                  <Route path="/essay-help/:universityId" element={<EssayHelpPage />} />
                  <Route path="/financial-aid" element={<FinancialAidComparison />} />
                  {/* Catch-all: an unknown path used to render a BLANK page
                      (no element matched, nothing painted) — exactly what a
                      student sees if an emailed link goes out ahead of its
                      deploy. Land them on the Launchpad instead of a void. */}
                  <Route path="*" element={<Navigate to="/launchpad" replace />} />
                </Route>
              </Routes>
              </Suspense>
              <UpgradeModal />
              </DetailViewProvider>
              </SidebarProvider>
            </ToastProvider>
          </ApproachProvider>
        </PaymentProvider>
        </FeatureFlagsProvider>
      </AuthProvider>
    </Router>
    </RouteErrorBoundary>
  );
}

export default App;
